import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  ArrowLeft,
  Plus,
  MoreVertical,
  Pencil,
  Trash2,
  Cog,
  Calendar,
  MapPin,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Mail,
  Phone,
  Building2,
  UserPlus,
  Loader2,
  Send,
  Wrench,
  History,
  Building,
} from "lucide-react";
import { toast } from "sonner";
import CustomerServices from "../components/CustomerServices";
import CustomerVenues from "../components/CustomerVenues";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomerDetail = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [installations, setInstallations] = useState([]);
  const [systems, setSystems] = useState([]);
  const [services, setServices] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingLink, setSendingLink] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editCustomerSheetOpen, setEditCustomerSheetOpen] = useState(false);
  const [savingCustomer, setSavingCustomer] = useState(false);
  const [editingInstallation, setEditingInstallation] = useState(null);
  const [customerFormData, setCustomerFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company_name: "",
    vat_number: "",
    address: {
      street: "",
      city: "",
      state: "",
      postal_code: "",
      country: "South Africa",
    },
    reminder_days_before: 7,
  });
  const [formData, setFormData] = useState({
    system_id: "",
    location_name: "",
    installation_date: "",
    last_service_date: "",
    service_interval_days: "",
    serial_number: "",
    notes: "",
  });

  const fetchData = async () => {
    try {
      const [customerRes, installationsRes, systemsRes, servicesRes, auditRes] = await Promise.all([
        axios.get(`${API}/customers/${customerId}`, { withCredentials: true }),
        axios.get(`${API}/customers/${customerId}/installations`, {
          withCredentials: true,
        }),
        axios.get(`${API}/installation-systems`, { withCredentials: true }),
        axios.get(`${API}/services`, { withCredentials: true }),
        axios.get(`${API}/customers/${customerId}/audit-log`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setCustomer(customerRes.data);
      setInstallations(installationsRes.data);
      setSystems(systemsRes.data);
      setServices(servicesRes.data);
      setAuditLogs(auditRes.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load customer details");
      navigate("/customers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [customerId]);

  const openEditCustomerSheet = () => {
    setCustomerFormData({
      name: customer.name || "",
      email: customer.email || "",
      phone: customer.phone || "",
      company_name: customer.company_name || "",
      vat_number: customer.vat_number || "",
      address: {
        street: customer.address?.street || "",
        city: customer.address?.city || "",
        state: customer.address?.state || "",
        postal_code: customer.address?.postal_code || "",
        country: customer.address?.country || "South Africa",
      },
      reminder_days_before: customer.reminder_days_before || 7,
    });
    setEditCustomerSheetOpen(true);
  };

  const handleSaveCustomer = async (e) => {
    e.preventDefault();
    setSavingCustomer(true);
    try {
      await axios.put(
        `${API}/customers/${customerId}`,
        customerFormData,
        { withCredentials: true }
      );
      toast.success("Customer profile updated successfully");
      setEditCustomerSheetOpen(false);
      fetchData();
    } catch (error) {
      console.error("Error updating customer:", error);
      toast.error(error.response?.data?.detail || "Failed to update customer");
    } finally {
      setSavingCustomer(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        customer_id: customerId,
        service_interval_days: formData.service_interval_days
          ? parseInt(formData.service_interval_days)
          : null,
      };

      if (editingInstallation) {
        await axios.put(
          `${API}/customers/${customerId}/installations/${editingInstallation.installation_id}`,
          payload,
          { withCredentials: true }
        );
        toast.success("Installation updated");
      } else {
        await axios.post(
          `${API}/customers/${customerId}/installations`,
          payload,
          { withCredentials: true }
        );
        toast.success("Installation added");
      }
      setSheetOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error("Error saving installation:", error);
      toast.error(
        error.response?.data?.detail || "Failed to save installation"
      );
    }
  };

  const handleEdit = (installation) => {
    setEditingInstallation(installation);
    setFormData({
      system_id: installation.system_id,
      location_name: installation.location_name || "",
      installation_date: installation.installation_date || "",
      last_service_date: installation.last_service_date || "",
      service_interval_days: installation.service_interval_days?.toString() || "",
      serial_number: installation.serial_number || "",
      notes: installation.notes || "",
    });
    setSheetOpen(true);
  };

  const handleDelete = async (installation) => {
    if (!confirm(`Delete installation "${installation.system_name}"?`)) return;
    try {
      await axios.delete(
        `${API}/customers/${customerId}/installations/${installation.installation_id}`,
        { withCredentials: true }
      );
      toast.success("Installation deleted");
      fetchData();
    } catch (error) {
      console.error("Error deleting installation:", error);
      toast.error("Failed to delete installation");
    }
  };

  const resetForm = () => {
    setEditingInstallation(null);
    setFormData({
      system_id: "",
      location_name: "",
      installation_date: "",
      last_service_date: "",
      service_interval_days: "",
      serial_number: "",
      notes: "",
    });
  };

  const getServiceStatus = (installation) => {
    if (!installation.next_service_due) {
      return { label: "No Service Data", color: "bg-slate-100 text-slate-600" };
    }
    const today = new Date();
    const dueDate = new Date(installation.next_service_due);
    const daysUntilDue = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));

    if (daysUntilDue < 0) {
      return { label: "Overdue", color: "bg-red-100 text-red-700", icon: AlertTriangle };
    }
    if (daysUntilDue <= 14) {
      return { label: "Due Soon", color: "bg-amber-100 text-amber-700", icon: Clock };
    }
    return { label: "Up to Date", color: "bg-emerald-100 text-emerald-700", icon: CheckCircle2 };
  };

  const handleSendRegistrationLink = async () => {
    setSendingLink(true);
    try {
      const response = await axios.post(
        `${API}/customers/${customerId}/send-registration-link`,
        {},
        { withCredentials: true }
      );
      toast.success(response.data.message || "Registration link sent!");
      fetchData(); // Refresh to update user_id status
    } catch (error) {
      console.error("Error sending registration link:", error);
      toast.error(error.response?.data?.detail || "Failed to send registration link");
    } finally {
      setSendingLink(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 md:p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-48"></div>
          <div className="h-48 bg-slate-200 rounded-sm"></div>
        </div>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="p-6 md:p-8">
        <p>Customer not found</p>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8" data-testid="customer-detail-page">
      {/* Header */}
      <div className="mb-8">
        <Button
          variant="ghost"
          onClick={() => navigate("/customers")}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Customers
        </Button>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center">
              <span className="text-2xl text-emerald-700 font-semibold">
                {customer.name.charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <h1 className="text-3xl font-semibold text-slate-900">
                {customer.name}
              </h1>
              {customer.company_name && (
                <p className="text-slate-500">{customer.company_name}</p>
              )}
              {customer.user_id ? (
                <Badge className="mt-1 rounded-sm bg-emerald-100 text-emerald-700">
                  <CheckCircle2 className="h-3 w-3 mr-1" />
                  Account Created
                </Badge>
              ) : (
                <Badge className="mt-1 rounded-sm bg-amber-100 text-amber-700">
                  <UserPlus className="h-3 w-3 mr-1" />
                  No Account
                </Badge>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="rounded-sm"
              onClick={openEditCustomerSheet}
              data-testid="edit-customer-btn"
            >
              <Pencil className="h-4 w-4 mr-2" />
              Edit Customer
            </Button>
            {!customer.user_id && (
              <Button
                variant="outline"
                className="rounded-sm"
                onClick={handleSendRegistrationLink}
                disabled={sendingLink}
                data-testid="send-registration-link-btn"
              >
                {sendingLink ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                {sendingLink ? "Sending..." : "Send Registration Link"}
              </Button>
            )}
            <Button
              className="rounded-sm bg-slate-900 hover:bg-slate-800"
              onClick={() => setSheetOpen(true)}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Installation
            </Button>
          </div>
        </div>
      </div>

      {/* Customer Info Card */}
      <Card className="rounded-sm border-slate-200 mb-6">
        <CardHeader>
          <CardTitle className="text-lg">Customer Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm">
                <Mail className="h-4 w-4 text-slate-400" />
                <span>{customer.email}</span>
              </div>
              {customer.phone && (
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="h-4 w-4 text-slate-400" />
                  <span>{customer.phone}</span>
                </div>
              )}
              {customer.company_name && (
                <div className="flex items-center gap-2 text-sm">
                  <Building2 className="h-4 w-4 text-slate-400" />
                  <span>{customer.company_name}</span>
                </div>
              )}
            </div>
            {customer.address && (
              <div className="flex items-start gap-2 text-sm">
                <MapPin className="h-4 w-4 text-slate-400 mt-0.5" />
                <div>
                  {customer.address.street && <p>{customer.address.street}</p>}
                  <p>
                    {[customer.address.city, customer.address.state]
                      .filter(Boolean)
                      .join(", ")}
                  </p>
                  <p>
                    {[customer.address.postal_code, customer.address.country]
                      .filter(Boolean)
                      .join(", ")}
                  </p>
                </div>
              </div>
            )}
            <div>
              <p className="text-sm text-slate-500">Reminder Setting</p>
              <p className="font-medium">
                {customer.reminder_days_before} days before service due
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabbed Content: Venues, Services, Bookings, Installations, Settings, History */}
      <Tabs defaultValue="bookings" className="w-full">
        <TabsList className="grid w-full grid-cols-5 mb-6">
          <TabsTrigger value="bookings" className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            Service Bookings
          </TabsTrigger>
          <TabsTrigger value="venues" className="flex items-center gap-2">
            <Building className="h-4 w-4" />
            Venues
          </TabsTrigger>
          <TabsTrigger value="installations" className="flex items-center gap-2">
            <Cog className="h-4 w-4" />
            Installations
          </TabsTrigger>
          <TabsTrigger value="settings" className="flex items-center gap-2">
            <Wrench className="h-4 w-4" />
            Assigned Services
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2">
            <History className="h-4 w-4" />
            Edit History
          </TabsTrigger>
        </TabsList>

        {/* Service Bookings Tab */}
        <TabsContent value="bookings">
          <CustomerServices 
            customerId={customerId} 
            customerName={customer.name}
            installations={installations}
          />
        </TabsContent>

        {/* Venues Tab */}
        <TabsContent value="venues">
          <CustomerVenues customerId={customerId} />
        </TabsContent>

        {/* Installations Tab */}
        <TabsContent value="installations">
          <Card className="rounded-sm border-slate-200">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">
                Installations ({installations.length})
              </CardTitle>
              <Button
                className="rounded-sm bg-slate-900 hover:bg-slate-800"
                size="sm"
                onClick={() => setSheetOpen(true)}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Installation
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              {installations.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>System</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Installed</TableHead>
                      <TableHead>Last Service</TableHead>
                      <TableHead>Next Due</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="w-12"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {installations.map((inst) => {
                      const status = getServiceStatus(inst);
                      const StatusIcon = status.icon;
                      return (
                        <TableRow key={inst.installation_id}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-indigo-100 rounded-sm flex items-center justify-center">
                                <Cog className="h-5 w-5 text-indigo-600" />
                              </div>
                              <div>
                                <p className="font-medium">{inst.system_name}</p>
                                {inst.serial_number && (
                                  <p className="text-xs text-slate-400">
                                    S/N: {inst.serial_number}
                                  </p>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>{inst.location_name || "-"}</TableCell>
                          <TableCell>{inst.installation_date || "-"}</TableCell>
                          <TableCell>{inst.last_service_date || "-"}</TableCell>
                          <TableCell>{inst.next_service_due || "-"}</TableCell>
                          <TableCell>
                            <Badge className={`rounded-sm ${status.color}`}>
                              {StatusIcon && <StatusIcon className="h-3 w-3 mr-1" />}
                              {status.label}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-8 w-8">
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleEdit(inst)}>
                                  <Pencil className="mr-2 h-4 w-4" />
                                  Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => handleDelete(inst)}
                                  className="text-red-600 focus:text-red-600"
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-12">
                  <Cog className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No installations yet</p>
                  <Button
                    variant="outline"
                    onClick={() => setSheetOpen(true)}
                    className="mt-4 rounded-sm"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Installation
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Assigned Services Tab */}
        <TabsContent value="settings">
          <Card className="rounded-sm border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg">Assigned Services</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-500 mb-4">
                Services this customer can book. Toggle to enable or disable.
              </p>
              {services.length === 0 ? (
                <div className="text-center py-6 text-slate-500">
                  <Cog className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No services defined yet</p>
                  <Button
                    variant="outline"
                    onClick={() => navigate("/services")}
                    className="mt-2 rounded-sm"
                    size="sm"
                  >
                    Go to Services
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {services.map((service) => {
                    const isAssigned = (customer.assigned_services || []).includes(service.service_id);
                    return (
                      <div
                        key={service.service_id}
                        className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                          isAssigned
                            ? "border-emerald-300 bg-emerald-50"
                            : "border-slate-200 bg-slate-50 hover:border-slate-300"
                        }`}
                        onClick={async () => {
                          try {
                            const currentServices = customer.assigned_services || [];
                            const newServices = isAssigned
                              ? currentServices.filter(id => id !== service.service_id)
                              : [...currentServices, service.service_id];
                            
                            await axios.put(
                              `${API}/customers/${customerId}`,
                              { assigned_services: newServices },
                              { withCredentials: true }
                            );
                            setCustomer({ ...customer, assigned_services: newServices });
                            toast.success(isAssigned ? "Service removed" : "Service assigned");
                          } catch (error) {
                            toast.error("Failed to update services");
                          }
                        }}
                        data-testid={`service-toggle-${service.service_id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-sm">{service.name}</p>
                            <p className="text-xs text-slate-500">
                              {service.duration_minutes} min
                            </p>
                          </div>
                          <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                            isAssigned ? "bg-emerald-500" : "bg-slate-300"
                          }`}>
                            {isAssigned && <CheckCircle2 className="h-3 w-3 text-white" />}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Edit History Tab */}
        <TabsContent value="history">
          <Card className="rounded-sm border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg">Edit History</CardTitle>
            </CardHeader>
            <CardContent>
              {auditLogs.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <History className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p>No edit history available</p>
                  <p className="text-sm">Changes to this customer profile will be logged here.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {auditLogs.map((log) => (
                    <div key={log.audit_id} className="border-l-2 border-slate-200 pl-4 pb-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm font-medium">{log.updated_by_name}</span>
                        <Badge variant="outline" className="rounded-sm text-xs">
                          {log.updated_by_role}
                        </Badge>
                        <span className="text-xs text-slate-500 ml-auto">
                          {new Date(log.timestamp).toLocaleString()}
                        </span>
                      </div>
                      <div className="space-y-1">
                        {log.changes.map((change, idx) => (
                          <div key={`${change.field}-${idx}`} className="text-sm text-slate-600 flex items-center gap-2">
                            <span className="font-medium capitalize">{change.field.replace(/_/g, " ")}:</span>
                            <span className="text-red-600 line-through">{change.old_value || "—"}</span>
                            <span className="text-slate-400">→</span>
                            <span className="text-emerald-600">{change.new_value || "—"}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add/Edit Installation Sheet */}
      <Sheet
        open={sheetOpen}
        onOpenChange={(open) => {
          setSheetOpen(open);
          if (!open) resetForm();
        }}
      >
        <SheetContent className="sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>
              {editingInstallation ? "Edit Installation" : "Add Installation"}
            </SheetTitle>
            <SheetDescription>
              {editingInstallation
                ? "Update installation details"
                : "Add a new installation for this customer"}
            </SheetDescription>
          </SheetHeader>
          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            <div className="space-y-2">
              <Label>System Type *</Label>
              <Select
                value={formData.system_id}
                onValueChange={(value) =>
                  setFormData({ ...formData, system_id: value })
                }
                required
              >
                <SelectTrigger className="rounded-sm">
                  <SelectValue placeholder="Select system type" />
                </SelectTrigger>
                <SelectContent>
                  {systems.map((sys) => (
                    <SelectItem key={sys.system_id} value={sys.system_id}>
                      {sys.name} ({sys.service_interval_label})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="location_name">Location Name</Label>
              <Input
                id="location_name"
                value={formData.location_name}
                onChange={(e) =>
                  setFormData({ ...formData, location_name: e.target.value })
                }
                placeholder="e.g., Main Bar, Kitchen"
                className="rounded-sm"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="installation_date">Installation Date</Label>
                <Input
                  id="installation_date"
                  type="date"
                  value={formData.installation_date}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      installation_date: e.target.value,
                    })
                  }
                  className="rounded-sm"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_service_date">Last Service Date</Label>
                <Input
                  id="last_service_date"
                  type="date"
                  value={formData.last_service_date}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      last_service_date: e.target.value,
                    })
                  }
                  className="rounded-sm"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="service_interval">
                  Service Interval (days)
                </Label>
                <Input
                  id="service_interval"
                  type="number"
                  min="1"
                  value={formData.service_interval_days}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      service_interval_days: e.target.value,
                    })
                  }
                  placeholder="Use system default"
                  className="rounded-sm"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="serial_number">Serial Number</Label>
                <Input
                  id="serial_number"
                  value={formData.serial_number}
                  onChange={(e) =>
                    setFormData({ ...formData, serial_number: e.target.value })
                  }
                  placeholder="Optional"
                  className="rounded-sm"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) =>
                  setFormData({ ...formData, notes: e.target.value })
                }
                placeholder="Additional notes"
                rows={2}
                className="rounded-sm"
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setSheetOpen(false);
                  resetForm();
                }}
                className="flex-1 rounded-sm"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 rounded-sm bg-slate-900 hover:bg-slate-800"
              >
                {editingInstallation ? "Update" : "Add"}
              </Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>

      {/* Edit Customer Sheet */}
      <Sheet open={editCustomerSheetOpen} onOpenChange={setEditCustomerSheetOpen}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Edit Customer Profile</SheetTitle>
            <SheetDescription>
              Update customer information. Changes will be logged and the customer will be notified.
            </SheetDescription>
          </SheetHeader>
          <form onSubmit={handleSaveCustomer} className="space-y-4 mt-6">
            <div className="space-y-2">
              <Label htmlFor="cust_name">Name *</Label>
              <Input
                id="cust_name"
                value={customerFormData.name}
                onChange={(e) => setCustomerFormData({ ...customerFormData, name: e.target.value })}
                required
                className="rounded-sm"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cust_email">Email *</Label>
              <Input
                id="cust_email"
                type="email"
                value={customerFormData.email}
                onChange={(e) => setCustomerFormData({ ...customerFormData, email: e.target.value })}
                required
                className="rounded-sm"
              />
              <p className="text-xs text-slate-500">Changing email will also update login credentials if customer has a portal account.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="cust_phone">Phone</Label>
              <Input
                id="cust_phone"
                value={customerFormData.phone}
                onChange={(e) => setCustomerFormData({ ...customerFormData, phone: e.target.value })}
                className="rounded-sm"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cust_company">Company Name</Label>
              <Input
                id="cust_company"
                value={customerFormData.company_name}
                onChange={(e) => setCustomerFormData({ ...customerFormData, company_name: e.target.value })}
                className="rounded-sm"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cust_vat">VAT Number</Label>
              <Input
                id="cust_vat"
                value={customerFormData.vat_number}
                onChange={(e) => setCustomerFormData({ ...customerFormData, vat_number: e.target.value })}
                className="rounded-sm"
              />
            </div>
            <div className="border-t pt-4">
              <p className="text-sm font-medium mb-3">Address</p>
              <div className="space-y-3">
                <Input
                  placeholder="Street"
                  value={customerFormData.address.street}
                  onChange={(e) => setCustomerFormData({
                    ...customerFormData,
                    address: { ...customerFormData.address, street: e.target.value }
                  })}
                  className="rounded-sm"
                />
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    placeholder="City"
                    value={customerFormData.address.city}
                    onChange={(e) => setCustomerFormData({
                      ...customerFormData,
                      address: { ...customerFormData.address, city: e.target.value }
                    })}
                    className="rounded-sm"
                  />
                  <Input
                    placeholder="State/Province"
                    value={customerFormData.address.state}
                    onChange={(e) => setCustomerFormData({
                      ...customerFormData,
                      address: { ...customerFormData.address, state: e.target.value }
                    })}
                    className="rounded-sm"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    placeholder="Postal Code"
                    value={customerFormData.address.postal_code}
                    onChange={(e) => setCustomerFormData({
                      ...customerFormData,
                      address: { ...customerFormData.address, postal_code: e.target.value }
                    })}
                    className="rounded-sm"
                  />
                  <Input
                    placeholder="Country"
                    value={customerFormData.address.country}
                    onChange={(e) => setCustomerFormData({
                      ...customerFormData,
                      address: { ...customerFormData.address, country: e.target.value }
                    })}
                    className="rounded-sm"
                  />
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="cust_reminder">Reminder Days Before Service</Label>
              <Input
                id="cust_reminder"
                type="number"
                min="1"
                max="30"
                value={customerFormData.reminder_days_before}
                onChange={(e) => setCustomerFormData({ ...customerFormData, reminder_days_before: parseInt(e.target.value) || 7 })}
                className="rounded-sm"
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setEditCustomerSheetOpen(false)}
                className="flex-1 rounded-sm"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={savingCustomer}
                className="flex-1 rounded-sm bg-slate-900 hover:bg-slate-800"
              >
                {savingCustomer ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</>
                ) : (
                  "Save Changes"
                )}
              </Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default CustomerDetail;
